<template>
  <div class="main-container">
    <div
      class="selection-list"
      v-if="activeTab === 'Intrassession' || isUserDoctor || activeTab === 'SessionComparison'"
    >
      <ChartUsersSelection
        class="mb-3 background-container"
        v-if="isUserDoctor"
        :patientsList="users"
        @userClickedCharts="getUserSessions"
      ></ChartUsersSelection>
      <ChartFilterMenusVue
        class="mt-3 background-container"
        v-if="activeTab === 'Intrassession'"
        @filtersChange="loadDataBasedOnFilters"
        @sessionsTagChange="loadDataBasedOnTags"
      ></ChartFilterMenusVue>
    </div>
    <div class="vertical-container">
      <div class="chart-data scroller background-container">
        <div class="charts-box background-container" v-if="selectedUser">
          <el-tabs
            :model-value="activeTab"
            class="demo-tabs mt-3"
            @tab-click="handleTabsClick"
          >
            <el-tab-pane label="Intrassession charts" name="Intrassession" :lazy="true">
              <SessionsDropDown
                v-if="!loadingSessions && sessions"
                :sessions="sessions"
                :selectedSession="selectedSession"
                @sessionClicked="getDeviceType"
              />
              <div>
                <SessionInfo
                  class="ml-2 mt-4 mb-3"
                  v-if="selectedSession"
                  :session="selectedSession"
                ></SessionInfo>
                <div v-if="shouldRenderGloveCharts">
                  <!-- Tab Navigation -->
                  <ul class="nav nav-tabs" role="tablist">
                    <li
                      class="nav-item"
                      v-for="(sensorType, index) in gloveSensorTypes"
                      :key="sensorType"
                    >
                      <a
                        class="nav-link"
                        :class="{ active: index === activeChartIndex }"
                        @click="setActiveChartIndex(index)"
                        data-toggle="tab"
                        :href="'#' + sensorType"
                      >
                        {{ sensorType }}
                      </a>
                    </li>
                  </ul>

                  <!-- Tab Content -->
                  <div class="tab-content">
                    <div
                      class="tab-pane"
                      :class="{ active: sensorType === activeSensorType }"
                      :id="sensorType"
                      v-for="sensorType in gloveSensorTypes"
                      :key="sensorType"
                    >
                      <keep-alive>
                        <LineChart
                          v-if="gloveData.has(`glove-${sensorType.toLowerCase()}`) && !sensorType.includes('Sensors')"
                          :id="`glove-${sensorType.toLowerCase()}`"
                          :title="gloveData.get(`glove-${sensorType.toLowerCase()}`).title"
                          :series="gloveData.get(`glove-${sensorType.toLowerCase()}`).data"
                          :movements="sessionMovements"
                          @chartsLoaded="chartsLoaded"
                          :sessionId="selectedSession.id"
                        />
                      </keep-alive>
                      <keep-alive>
                        <LineChartWithToggleVue
                          v-if="gloveData.has(`glove-${sensorType.toLowerCase()}`) && sensorType.includes('Sensors')"
                          :id="`glove-${sensorType.toLowerCase()}`"
                          :title="gloveData.get(`glove-${sensorType.toLowerCase()}`).title"
                          :series="gloveData.get(`glove-${sensorType.toLowerCase()}`).data"
                          :percentageChangeSeries="gloveData.get(`glove-${sensorType.toLowerCase()}-percentageChangeData`).data"
                          :movements="sessionMovements"
                          :sessionId="selectedSession.id"
                          @chartsLoaded="chartsLoaded"
                        />
                      </keep-alive>
                    </div>
                  </div>
                </div>
                <!-- Knee Sleeve and Pants -->
                <div v-if="shouldRenderKneeBraceCharts">
                  <!-- Tab Navigation -->
                  <ul class="nav nav-tabs" role="tablist">
                    <li
                      class="nav-item"
                      v-for="(sensorType, index) in kneeSleeveSensorTypes"
                      :key="sensorType"
                    >
                      <a
                        class="nav-link"
                        :class="{ active: index === activeChartIndex }"
                        @click="setActiveChartIndex(index)"
                        data-toggle="tab"
                        :href="'#' + sensorType"
                      >
                        {{ sensorType }}
                      </a>
                    </li>
                  </ul>

                  <!-- Tab Content -->
                  <div class="tab-content">
                    <div
                      class="tab-pane"
                      :class="{ active: sensorType === activeSensorType }"
                      :id="sensorType"
                      v-for="sensorType in kneeSleeveSensorTypes"
                      :key="sensorType"
                    >
                      <keep-alive>
                        <LineChart
                          v-if="kneeBraceData.has(`knee-${sensorType.toLowerCase()}`) && !sensorType.includes('Sensors')"
                          :id="`knee-${sensorType.toLowerCase()}`"
                          :title="kneeBraceData.get(`knee-${sensorType.toLowerCase()}`).title"
                          :series="kneeBraceData.get(`knee-${sensorType.toLowerCase()}`).data"
                          :movements="sessionMovements"
                          @chartsLoaded="chartsLoaded"
                          :sessionId="selectedSession.id"
                        />
                      </keep-alive>
                      <keep-alive>
                        <LineChartWithToggleVue
                          v-if="kneeBraceData.has(`knee-${sensorType.toLowerCase()}`) && sensorType.includes('Sensors')"
                          :id="`knee-${sensorType.toLowerCase()}`"
                          :title="kneeBraceData.get(`knee-${sensorType.toLowerCase()}`).title"
                          :series="kneeBraceData.get(`knee-${sensorType.toLowerCase()}`).data"
                          :percentageChangeSeries="kneeBraceData.get(`knee-${sensorType.toLowerCase()}-percentageChangeData`).data"
                          :movements="sessionMovements"
                          @chartsLoaded="chartsLoaded"
                          :sessionId="selectedSession.id"
                        />
                      </keep-alive>
                    </div>
                  </div>
                </div>

                <!-- Neat -->
                <div v-if="shouldRenderNeatCharts">
                  <!-- Tab Navigation -->
                  <ul class="nav nav-tabs" role="tablist">
                    <li
                      class="nav-item"
                      v-for="(chart, index) in neatData"
                      :key="chart.id"
                    >
                      <a
                        class="nav-link"
                        :class="{ active: index === 0 }"
                        data-toggle="tab"
                        :href="'#' + chart.id"
                      >
                        {{ chart.title }}
                      </a>
                    </li>
                  </ul>
                  <!-- Tab Content -->
                  <div class="tab-content">
                    <div
                      class="tab-pane"
                      :class="{ active: index === 0 }"
                      :id="chart.id"
                      v-for="(chart, index) in neatData"
                      :key="chart.id"
                    >
                      <LineChart
                        :id="chart.id"
                        :title="chart.title"
                        :series="chart.data"
                        :movements="sessionMovements"
                        @chartsLoaded="chartsLoaded"
                        :sessionId="selectedSession.id"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="shouldRenderBandCharts">
                  <!-- Tab Navigation -->
                  <ul class="nav nav-tabs" role="tablist">
                    <li
                      class="nav-item"
                      v-for="(chart, index) in bandData"
                      :key="chart.id"
                    >
                      <a
                        class="nav-link"
                        :class="{ active: index === 0 }"
                        data-toggle="tab"
                        :href="'#' + chart.id"
                      >
                        {{ chart.title }}
                      </a>
                    </li>
                  </ul>
                  <!-- Tab Content -->
                  <div class="tab-content">
                    <div
                      class="tab-pane"
                      :class="{ active: index === 0 }"
                      :id="chart.id"
                      v-for="(chart, index) in bandData"
                      :key="chart.id"
                    >
                      <LineChart
                        :id="chart.id"
                        :title="chart.title"
                        :series="chart.data"
                        :movements="sessionMovements"
                        @chartsLoaded="chartsLoaded"
                        :sessionId="selectedSession.id"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="shouldRenderTestCharts">
                  <!-- Tab Navigation -->
                  <ul class="nav nav-tabs" role="tablist">
                    <li
                      class="nav-item"
                      v-for="(chart, index) in testData"
                      :key="chart.id"
                    >
                      <a
                        class="nav-link"
                        :class="{ active: index === 0 }"
                        data-toggle="tab"
                        :href="'#' + chart.id"
                      >
                        {{ chart.title }}
                      </a>
                    </li>
                  </ul>
                  <!-- Tab Content -->
                  <div class="tab-content">
                    <div
                      class="tab-pane"
                      :class="{ active: index === 0 }"
                      :id="chart.id"
                      v-for="(chart, index) in testData"
                      :key="chart.id"
                    >
                      <LineChart
                        :id="chart.id"
                        :title="chart.title"
                        :series="chart.data"
                        :movements="sessionMovements"
                        @chartsLoaded="chartsLoaded"
                        :sessionId="selectedSession.id"
                      />
                    </div>
                  </div>
                </div>
                <Loading
                  class="loader"
                  :active="chartsLoading"
                  :is-full-page="false"
                  color="var(--accent-color)"
                  loader="bars"
                />

                <div
                  v-if="shouldRenderBandCharts || shouldRenderGloveCharts || shouldRenderKneeBraceCharts || shouldRenderNeatCharts || shouldRenderTestCharts"
                >
                  <div class="export-box">
                    <span class="mr-2 text"
                      >Export complete sensor data to Excel:
                    </span>
                    <input
                      type="checkbox"
                      id="left"
                      value="Left sensor"
                      v-model="leftChecked"
                      v-if="leftCSVData.length"
                    />
                    <label
                      v-if="leftCSVData.length"
                      for="left"
                      class="ml-2 mr-2"
                      >Left sensors</label
                    >
                    <input
                      type="checkbox"
                      id="right"
                      value="Right sensor"
                      v-model="rightChecked"
                      v-if="rightCSVData.length"
                    />
                    <label
                      v-if="rightCSVData.length"
                      for="right"
                      class="ml-2 mr-2"
                      >Right sensors</label
                    >
                    <button @click="downloadData" class="btn btn-primary ml-2">
                      Download
                    </button>
                  </div>
                  <div class="notes-section">
                    <h5 class="mb-3">Session notes</h5>
                    <b-form-group>
                      <b-form-textarea
                        v-model="userNotes"
                        rows="5"
                        :readonly="!isEditing"
                        @click="enableEditing"
                      ></b-form-textarea>
                    </b-form-group>
                    <b-button
                      @click="saveNotes"
                      class="saveNotesBtn"
                      variant="primary"
                      :disabled="updatingNotes || !isEditing"
                    >
                      <span class="white-text" v-if="updatingNotes"
                        ><i class="fas fa-spinner fa-spin"></i> Saving...</span
                      >
                      <span class="white-text" v-else>Save Notes</span>
                    </b-button>
                    <div v-if="isUserDoctor" class="float-right-box">
                      <button
                        :disabled="removingSession"
                        @click="showDeleteSessionModal = true"
                        class="btn btn-secondary ml-2"
                      >
                        <span class="white-text" v-if="removingSession"
                          ><i class="fas fa-spinner fa-spin"></i>
                          Removing...</span
                        >
                        <span class="white-text" v-else
                          >Remove session data</span
                        >
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </el-tab-pane>
            <el-tab-pane label="Postsession charts" name="Postsession" :lazy="true">
              <el-menu
                class="el-menu-demo"
                mode="horizontal"
                :ellipsis="false"
                menu-trigger="click"
                :unique-opened="true"
                @select="handleSelect"
              >
                <div class="flex-grow" />
                <el-sub-menu index="0">
                  <template
                    #title
                    >{{ postsessionDevices[selectedPostsessionDevice] }}</template
                  >
                  <el-menu-item
                    v-for="num in unselectedPostsessionDevice"
                    :key="num"
                    :index="'0-' + num"
                    >{{ postsessionDevices[num] }}
                  </el-menu-item>
                </el-sub-menu>
                <el-sub-menu index="1">
                  <template #title>
                    <el-icon><Calendar /></el-icon>
                    {{ postsessionTimeRanges[parseInt(selectedPostsessionTimeRange)]}}</template
                  >
                  <el-menu-item
                    :index="rangeIndex"
                    v-for="rangeIndex in unselectedPostsessionsTimeRanges"
                    :key="rangeIndex"
                  >
                    {{ postsessionTimeRanges[rangeIndex] }}
                  </el-menu-item>
                </el-sub-menu>
              </el-menu>
              <div id="chart-container">
                <div
                  v-for="(group, groupName) in groupedChartData"
                  :key="groupName"
                  class="chart-group"
                >
                  <RangeChart
                    v-for="chart in group.charts"
                    :key="chart.id"
                    :id="chart.id"
                    :title="chart.title"
                    :series="chart.series"
                    :avgs="chart.avgs"
                    :avgs2="chart.avgs2"
                  />
                </div>
              </div>

              <Loading
                :active="removeSessionInProgress"
                :is-full-page="false"
                color="purple"
                loader="bars"
                class="loader"
              />
            </el-tab-pane>

            <el-tab-pane label="Session Comparison" name="SessionComparison" :lazy="true">
              <SesssionsMultipleSelectionVue
                :userId="selectedUser.id"
                @onMultipleSessionsSelected="getMultipleSessionsData"
                @deselectComparsionSessions="isMultipleSessionsSelected = false"
              />
              <IntrasessionMultipleChartVue
                :deviceType="sessionComparsionDeviceType"
                :sessions="multipleSessionsSelected"
                :selectedUser="selectedUser"
                :sessionsSelected="isMultipleSessionsSelected"
                ref="intrasessionMultipleChart"
              ></IntrasessionMultipleChartVue>
            </el-tab-pane>

            <el-tab-pane label="Data download" name="Download" :lazy="true">
              <div class="mb-5 mt-3">
                <SessionRangeSelector
                  ref="sessionRangeSelector"
                  v-if="sessions"
                  :sessionsList="sessions"
                  :userDevices="selectedUser.devices"
                ></SessionRangeSelector>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
        <div v-else class="placeholder-container background-container">
          <div v-if="isUserDoctor">
            <i class="fa fa-user-plus placeholder-icon"></i>
            <h4>Select a patient to view sessions and charts.</h4>
            <p>
              Once a patient is selected, their sessions will appear here. Click
              on a session to view the associated charts.
            </p>
          </div>
          <div v-else>
            <Loading
              :active="loadingUserDoctorStatus"
              :is-full-page="false"
              color="var(--accent-color)"
              loader="bars"
              class="loader"
            />
          </div>
        </div>
      </div>
    </div>
    <Loading
      :active="loadingSessions && activeTab === 'Intrassession'"
      :is-full-page="true"
      color="var(--accent-color)"
      loader="bars"
      class="loader"
    />
    <b-modal
      id="my-modal"
      title="Warning"
      :visible="showDeleteSessionModal"
      @hide="onModalHide"
    >
      <p>
        Removing this session will remove all session data. Are you sure you
        want to continue?
      </p>
      <template #modal-footer>
        <b-button @click="removeSession" variant="danger">Delete</b-button>
        <b-button @click="showDeleteSessionModal = false">Cancel</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { xray } from '../../../config/pluginInit'
import getUsers from '../../../composables/getUsers'
import getSessions from '../../../composables/getSessions'
import getDevices from '../../../composables/getDevices'
import getSensorData from '../../../composables/getSensorData'
import processGloveData from './functions/processGloveData'
import processKneeBraceData from './functions/processKneeBraceData'
import processNeatData from './functions/processNeatData'
import processBandData from './functions/processBandData'
import processTestData from './functions/processTestData'
import processPostsessinsData from './functions/processPostsessionsData'
import SessionsDropDown from '../../../components/charts/SessionsDropDown.vue'
import LineChart from '../../../components/charts/LineChart.vue'
import RangeChart from '../../../components/charts/RangeChart.vue'
import exportAsCSV from './functions/SaveAsCSV'
import { computed, onMounted, ref, watch } from 'vue'
import firebase from '../../../../firebase'
import ChartUsersSelection from './ChartUsersSelection.vue'
import ChartFilterMenusVue from './ChartFilterMenus.vue'
import {
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs
} from '@firebase/firestore'
import { updateDocument } from '../../../services/useDocument'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/css/index.css'
import LineChartWithToggleVue from '../../../components/charts/LineChartWithToggle.vue'
import SessionInfo from './SessionInfo.vue'
import SessionRangeSelector from './SessionRangeSelector.vue'
import IntrasessionMultipleChartVue from './IntrasessionMultipleChart.vue'
import SesssionsMultipleSelectionVue from '../../../components/charts/SesssionsMultipleSelection.vue'

export default {
  name: 'ChartPage',
  components: {
    SessionsDropDown,
    LineChart,
    RangeChart,
    ChartUsersSelection,
    ChartFilterMenusVue,
    Loading,
    LineChartWithToggleVue,
    SessionInfo,
    SessionRangeSelector,
    IntrasessionMultipleChartVue,
    SesssionsMultipleSelectionVue
  },
  setup (props, context) {
    const { users, error, loadUsers, loadUserDetails } = getUsers()
    const { sessions, loadUserSessions, loadingSessions } = getSessions()
    const { getDeviceById } = getDevices()
    const { formatTime } = getSensorData()
    const { gloveData, shouldRenderGloveCharts, gloveSensorData, clearGloveSessionData, gloveSensorTypes, renderGloveSingleTypeSensorData } =
      processGloveData()
    const {
      kneeBraceData,
      shouldRenderKneeBraceCharts,
      renderSingleTypeSensorData,
      kneeBraceSensorData,
      clearKneeSleeveSessionData,
      kneeSleeveSensorTypes
    } = processKneeBraceData()
    const { neatData, shouldRenderNeatCharts, renderNeatCharts } =
      processNeatData()
    const { testData, shouldRenderTestCharts, renderTestCharts } =
      processTestData()
    const { bandData, shouldRenderBandCharts, renderBandCharts } =
      processBandData()
    const selectedUser = ref(null)
    const sessionMovements = ref(null)
    const selectedSession = ref(null)
    const activeTab = ref('Intrassession')
    const { leftCSVData, rightCSVData, getCSVdata, downloadCSV, downloadCSVForBothSides } = exportAsCSV()
    let userDevices = []
    const currentUser = firebase.firebaseAuth.currentUser
    const isUserDoctor = ref(false)
    const loadingUserDoctorStatus = ref(false)
    const selectedDevices = ref([])
    const selectedTimeRange = ref({ value: '' })
    const selectedTags = ref([])
    const showDeleteSessionModal = ref(false)
    const chartsLoading = ref(false)
    const activeChartIndex = ref(0)
    const selectedDeviceType = ref(null)
    const activeSensorType = ref(null)
    const currentDataForPlot = ref(null)
    const sessionRangeSelector = ref(null)
    const isMultipleSessionsSelected = ref(false)
    const multipleSessionsSelected = ref([])
    const sessionComparsionDeviceType = ref('Knee Sleeve')
    const intrasessionMultipleChart = ref(null)

    const combinedGloveData = computed(() => {
      return [...gloveSensorData.value]
    })

    const setActiveChartIndex = async (index) => {
      activeChartIndex.value = index
      if (selectedDeviceType.value === 'Pants' || selectedDeviceType.value === 'Knee Brace') {
        activeSensorType.value = kneeSleeveSensorTypes.value[activeChartIndex.value]
        if (kneeBraceData.value.has(`knee-${activeSensorType.value.toLowerCase()}`)) {
          return
        }
        chartsLoading.value = true
        await renderSingleTypeSensorData(selectedSession.value.id, userDevices, kneeSleeveSensorTypes.value[activeChartIndex.value])
      } else if (selectedDeviceType.value === 'Glove') {
        activeSensorType.value = gloveSensorTypes.value[activeChartIndex.value]
        if (gloveData.value.has(`glove-${activeSensorType.value.toLowerCase()}`)) {
          return
        }
        chartsLoading.value = true
        await renderGloveSingleTypeSensorData(selectedSession.value.id, userDevices, gloveSensorTypes.value[activeChartIndex.value])
      }
    }

    const chartsLoaded = () => {
      chartsLoading.value = false
    }

    onMounted(async () => {
      loadingUserDoctorStatus.value = true
      const ref = doc(firebase.db, 'doctors', currentUser.uid)
      const docSnapshot = await getDoc(ref)
      loadingUserDoctorStatus.value = false
      if (!docSnapshot.exists()) {
        isUserDoctor.value = false
        const currentUserDetails = await loadUserDetails(currentUser.uid)
        await getUserSessions(currentUserDetails)
      } else {
        isUserDoctor.value = true
        loadUsers()
      }
    })

    const getUserSessions = async (user) => {
      selectedUser.value = user
      selectedSession.value = null
      resetData()
      if (activeTab.value === 'Intrassession') {
        await loadUserSessions(user.id, selectedDevices.value, selectedTimeRange.value.value, selectedTags.value)
      } else if (activeTab.value === 'Postsession') {
        renderPostsessionCharts()
      } else if (activeTab.value === 'Download') {
        sessionRangeSelector.value.clearRanges()
        await loadUserSessions(user.id, selectedDevices.value, selectedTimeRange.value.value, selectedTags.value)
      }
    }

    const getDeviceType = async (session) => {
      selectedSession.value = session
      chartsLoading.value = true
      userDevices = selectedUser.value.devices
      const sessionDeviceIds = session.devices ? session.devices : []
      try {
        const sessionDevice = await getDeviceById(sessionDeviceIds[0])
        resetData()
        selectedDeviceType.value = sessionDevice.deviceType
        activeChartIndex.value = 0
        console.log()
        if (sessionDevice.deviceType === 'Glove') {
          await renderGloveSingleTypeSensorData(session.id, userDevices, gloveSensorTypes.value[activeChartIndex.value])
          activeSensorType.value = gloveSensorTypes.value[activeChartIndex.value]
          getCSVdata(sessionDevice.deviceType, selectedSession.value.id, selectedUser.value.devices)
        } else if (sessionDevice.deviceType === 'Knee Brace' || sessionDevice.deviceType === 'Knee Brace 2') {
          await renderSingleTypeSensorData(session.id, userDevices, kneeSleeveSensorTypes.value[activeChartIndex.value])
          activeSensorType.value = kneeSleeveSensorTypes.value[activeChartIndex.value]
          getCSVdata(sessionDevice.deviceType, selectedSession.value.id, selectedUser.value.devices)
        } else if (sessionDevice.deviceType === 'Neat') {
          await renderNeatCharts(session.id, userDevices)
          await getCSVdata(sessionDevice.deviceType, selectedSession.value.id, selectedUser.value.devices)
        } else if (sessionDevice.deviceType === 'Band') {
          await renderBandCharts(session.id, userDevices)
          await getCSVdata(sessionDevice.deviceType, selectedSession.value.id, selectedUser.value.devices)
        } else if (sessionDevice.deviceType === 'Pants') {
          await renderSingleTypeSensorData(session.id, userDevices, kneeSleeveSensorTypes.value[activeChartIndex.value])
          activeSensorType.value = kneeSleeveSensorTypes.value[activeChartIndex.value]
          getCSVdata(sessionDevice.deviceType, selectedSession.value.id, selectedUser.value.devices)
        } else if (sessionDevice.deviceType === 'Test') { // test device
          await renderTestCharts(session.id, userDevices)
          await getCSVdata(sessionDevice.deviceType, selectedSession.value.id, selectedUser.value.devices)
        }
        getMovementStartTimes(session.movements)
      } catch (error) {
        console.log('Error fetching device', error)
      }
    }

    const getMultipleSessionsData = (sessions, deviceType) => {
      multipleSessionsSelected.value = sessions
      sessionComparsionDeviceType.value = deviceType
      isMultipleSessionsSelected.value = true
    }

    const getMovementStartTimes = (movements) => {
      sessionMovements.value = movements.map((movement) => {
        return {
          x: formatTime(movement.startTime),
          borderColor: '#775DD0',
          label: {
            style: {
              background: '#775DD0',
              color: '#fff'
            },
            orientation: 'horizontal',
            text: movement.name
          }
        }
      })
      sessionMovements.value.shift()
    }
    const postsessionDevices = ['Glove', 'Knee sleeve', 'Pants']
    const postsessionTimeRanges = ['Last week', 'Last month', 'Last 6 months', 'Last year']
    const selectedPostsessionDevice = ref(0)
    const unselectedPostsessionDevice = ref([1, 2])
    const selectedPostsessionTimeRange = ref('0')
    const timeRanges = ['0', '1', '2', '3']
    const unselectedPostsessionsTimeRanges = ref(['1', '2', '3'])

    const handleSelect = (key) => {
      if (key === '0-0') {
        selectedPostsessionDevice.value = 0
        unselectedPostsessionDevice.value = [1, 2]
      } else if (key === '0-1') {
        selectedPostsessionDevice.value = 1
        unselectedPostsessionDevice.value = [0, 2]
      } else if (key === '0-2') {
        selectedPostsessionDevice.value = 2
        unselectedPostsessionDevice.value = [0, 1]
      } else {
        selectedPostsessionTimeRange.value = key
        unselectedPostsessionsTimeRanges.value = timeRanges.filter(range => range !== selectedPostsessionTimeRange.value)
      }
      renderPostsessionCharts()
    }

    const renderPostsessionCharts = async () => {
      if (selectedPostsessionDevice.value === 0) {
        shouldRenderKneeSleevePostsessionCharts.value = false
        shouldRenderGlovePostsessionCharts.value = true
        shouldRenderPantsPostsessionCharts.value = false
        await processPostsessionsDataForDeviceType('Glove', selectedUser.value.id, parseInt(selectedPostsessionTimeRange.value))
      } else if (selectedPostsessionDevice.value === 1) {
        shouldRenderGlovePostsessionCharts.value = false
        shouldRenderKneeSleevePostsessionCharts.value = true
        shouldRenderPantsPostsessionCharts.value = false
        await processPostsessionsDataForDeviceType('Knee Sleeve', selectedUser.value.id, parseInt(selectedPostsessionTimeRange.value))
      } else {
        shouldRenderGlovePostsessionCharts.value = false
        shouldRenderKneeSleevePostsessionCharts.value = false
        shouldRenderPantsPostsessionCharts.value = true
        await processPostsessionsDataForDeviceType('Pants', selectedUser.value.id, parseInt(selectedPostsessionTimeRange.value))
      }
    }

    const {
      groupedChartData,
      processPostsessionsDataForDeviceType
    } = processPostsessinsData()
    const shouldRenderGlovePostsessionCharts = ref(true)
    const shouldRenderKneeSleevePostsessionCharts = ref(false)
    const shouldRenderPantsPostsessionCharts = ref(false)

    const resetData = () => {
      selectedDeviceType.value = null
      activeSensorType.value = null
      neatData.value = []
      bandData.value = []
      sessionMovements.value = []
      groupedChartData.value = {}
      shouldRenderGloveCharts.value = false
      shouldRenderKneeBraceCharts.value = false
      shouldRenderNeatCharts.value = false
      shouldRenderBandCharts.value = false
      shouldRenderTestCharts.value = false
      leftCSVData.value = []
      rightCSVData.value = []
      leftChecked.value = false
      rightChecked.value = false
      clearKneeSleeveSessionData()
      clearGloveSessionData()
    }

    const handleTabsClick = async (tab) => {
      activeTab.value = tab.props.name
      if (tab.props.name !== 'Intrassession') {
        // clear old selections
        selectedDevices.value = []
        selectedTimeRange.value = []
        selectedTags.value = []
        resetData()
        selectedSession.value = null
      }
      if (tab.props.name === 'Postsession') {
        renderPostsessionCharts()
      } else if (tab.props.name === 'Intrassession') {
        await loadUserSessions(selectedUser.value.id, selectedDevices.value, selectedTimeRange.value.value, selectedTags.value)
      }
    }

    const leftChecked = ref(false)
    const rightChecked = ref(false)
    const downloadData = async () => {
      if (leftChecked.value && rightChecked.value) {
        await downloadCSVForBothSides(selectedSession.value.id, leftCSVData.value, rightCSVData.value, `${selectedSession.value.id}`)
      } else if (leftChecked.value) {
        await downloadCSV(selectedSession.value.id, leftCSVData.value, `${selectedSession.value.id}_left`)
      } else if (rightChecked.value) {
        await downloadCSV(selectedSession.value.id, rightCSVData.value, `${selectedSession.value.id}_right`)
      }
      leftChecked.value = false
      rightChecked.value = false
    }

    const sessionNotes = ref('')
    const isEditing = ref(false)
    const updatingNotes = ref(false)

    const saveNotes = async () => {
      updatingNotes.value = true
      const { handlelUpdate } = updateDocument('sessions', selectedSession.value.id)
      await handlelUpdate({ notes: sessionNotes.value })
      isEditing.value = false
      updatingNotes.value = false
    }

    const enableEditing = () => {
      if (!isEditing.value) {
        isEditing.value = true
      }
    }

    watch(selectedSession, () => {
      if (selectedSession.value) {
        sessionNotes.value = selectedSession.value.notes
      }
    })

    const clearIntraSessionCharts = () => {
      shouldRenderBandCharts.value = false
      shouldRenderGloveCharts.value = false
      shouldRenderKneeBraceCharts.value = false
      shouldRenderNeatCharts.value = false
      shouldRenderTestCharts.value = false
    }

    const loadDataBasedOnFilters = async (filters) => {
      clearIntraSessionCharts()
      selectedDevices.value = filters.devices
      selectedTimeRange.value = filters.timeRange
      if (selectedUser.value) {
        resetData()
        selectedSession.value = null
        await loadUserSessions(selectedUser.value.id, filters.devices, filters.timeRange.value, selectedTags.value)
      }
    }

    const loadDataBasedOnTags = async (tags) => {
      clearIntraSessionCharts()
      selectedTags.value = tags
      if (selectedUser.value) {
        resetData()
        selectedSession.value = null
        await loadUserSessions(selectedUser.value.id, selectedDevices.value, selectedTimeRange.value.value, tags)
      }
    }
    const removingSession = ref(false)
    const removeSession = async () => {
      try {
        showDeleteSessionModal.value = false
        removingSession.value = true
        const sessionId = selectedSession.value.id
        const userId = selectedUser.value.id

        const userDocRef = doc(firebase.db, 'users', userId)
        const sessionDocRef = doc(firebase.db, 'sessions', sessionId)
        // Delete documents in the "data_by_sensor" collection with a matching "sessionId"
        const dataBySensorCollectionRef = collection(firebase.db, 'data_by_sensor')
        const dataBySensorQuery = query(dataBySensorCollectionRef, where('sessionId', '==', sessionId))
        const querySnapshot = await getDocs(dataBySensorQuery)

        // Collect all delete promises
        const deletePromises = querySnapshot.docs.map((doc) => deleteDoc(doc.ref))

        // Wait for all deletes to complete
        await Promise.all(deletePromises)

        // Delete the session document
        await deleteDoc(sessionDocRef)
        const userDocSnap = await getDoc(userDocRef)
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data()

          // Filter out the session with the given sessionId
          const updatedSessions = userData.sessions.filter(session => session.sessionId !== sessionId)

          // Update the user document with the filtered sessions array
          await updateDoc(userDocRef, {
            sessions: updatedSessions
          })
        } else {
          console.log(`No user found with ID ${userId}`)
        }
        resetData()
        selectedSession.value = null
        await loadUserSessions(selectedUser.value.id, selectedDevices.value, selectedTimeRange.value.value, selectedTags.value)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (error) {
        console.log(error)
      } finally {
        removingSession.value = false
      }
    }

    return {
      users,
      sessions,
      error,
      selectedUser,
      sessionMovements,
      getUserSessions,
      getDeviceById,
      getDeviceType,
      gloveData,
      kneeBraceData,
      neatData,
      bandData,
      shouldRenderGloveCharts,
      shouldRenderKneeBraceCharts,
      shouldRenderNeatCharts,
      shouldRenderBandCharts,
      handleTabsClick,
      selectedPostsessionDevice,
      unselectedPostsessionDevice,
      postsessionDevices,
      handleSelect,
      postsessionTimeRanges,
      selectedPostsessionTimeRange,
      unselectedPostsessionsTimeRanges,
      shouldRenderGlovePostsessionCharts,
      shouldRenderKneeSleevePostsessionCharts,
      shouldRenderPantsPostsessionCharts,
      activeTab,
      rightCSVData,
      leftCSVData,
      leftChecked,
      rightChecked,
      downloadData,
      isUserDoctor,
      userNotes: sessionNotes,
      saveNotes,
      isEditing,
      enableEditing,
      updatingNotes,
      loadDataBasedOnFilters,
      loadDataBasedOnTags,
      loadingSessions,
      loadingUserDoctorStatus,
      removeSession,
      removingSession,
      showDeleteSessionModal,
      gloveSensorData,
      kneeBraceSensorData,
      shouldRenderTestCharts,
      testData,
      chartsLoading,
      chartsLoaded,
      selectedDevices,
      selectedTimeRange,
      combinedGloveData,
      activeChartIndex,
      setActiveChartIndex,
      kneeSleeveSensorTypes,
      activeSensorType,
      currentDataForPlot,
      gloveSensorTypes,
      selectedSession,
      selectedTags,
      sessionRangeSelector,
      isMultipleSessionsSelected,
      multipleSessionsSelected,
      getMultipleSessionsData,
      intrasessionMultipleChart,
      sessionComparsionDeviceType,
      groupedChartData
    }
  },
  mounted () {
    xray.index()
  }
}
</script>

<style>
.main-container {
  display: flex;
}
.selection-list {
  width: 20%;
  margin-left: 15px;
  min-width: 250px;
}
.chart-data {
  /* height: calc(120vh - 15rem); */
  margin: 0px 15px 15px 10px;
  background: #fff;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: scroll;
  overflow-x: hidden;
  min-height: 800px;
}
.charts-box {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

#chart-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-right: 20px;
}
@media (max-width: 767px) {
  .main-container {
    flex-direction: column;
    margin: 15px;
  }
  .selection-list {
    width: 100%;
    margin: 0;
  }
  .chart-data {
    margin: 0;
  }
}

.demo-tabs > .el-tabs__content {
  width: 100%;
}

.el-tabs {
  width: 90%;
}

.flex-grow {
  flex-grow: 1;
}

.export-box {
  float: right;
  margin-right: 30px;
  color: black;
  font-size: 15px;
  margin-bottom: 20px;
}

.float-right-box {
  float: right;
}

.notes-section {
  clear: right;
  margin-top: 20px;
  margin: 20px;
}

.hidden {
  display: none;
}

.placeholder-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 15rem);
  color: #888;
  background-color: #f9f9f9;
  text-align: center;
  padding: 20px;
  border: 1px dashed #ddd;
  width: 100%;
}

.placeholder-icon {
  font-size: 40px;
  margin-bottom: 10px;
}

h4 {
  margin: 10px 0;
}

p {
  font-size: 14px;
  line-height: 1.5;
}

.chart {
  width: 100%;
}

.charts-loader.vl-overlay {
  position: static !important;
}

.vertical-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.list-data {
  width: 100%;
  margin: 0px 15px 15px 10px;
  background: #fff;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: scroll;
  overflow-x: hidden;
}

.nav-tabs .nav-link {
  color: var(--muted-text-color) !important;
  border: 1px solid transparent; /* Adjust as needed */
}

.nav-tabs .nav-link.active,
.nav-tabs .nav-link:hover {
  color: var(--accent-color) !important;
  background-color: var(--light-grey) !important;
}

.saveNotesBtn {
  color: white !important;
}

.chart-group {
  width: 100%;
  margin-bottom: 10px;
  padding: 10px 0;
}
</style>
